import { buttonVariants } from "@/lib/components/Button/button-variants";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/lib/components/DropdownMenu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";
import { cn } from "@/lib/utils";
import { BackgroundMusicOwnershipFilterDto } from "@repo/api/background-music";
import { IconChevronDown } from "@tabler/icons-react";

export type CheckboxItem = {
  label: string;
  checked: boolean;
};

const ownershipLabels: Record<BackgroundMusicOwnershipFilterDto, string> = {
  ALL: "All Tracks",
  USER: "Team Tracks",
  LIBRARY: "Built-in Tracks",
  BEAT_SYNC: "Beat Sync",
};

export function Filters({
  ownership,
  onOwnershipChange,
  genreItems,
  moodItems,
  tagItems,
  onGenreChange,
  onMoodChange,
  onTagChange,
  hasActiveFilters,
  onClearAll,
}: {
  ownership: BackgroundMusicOwnershipFilterDto;
  onOwnershipChange: (value: BackgroundMusicOwnershipFilterDto) => void;
  genreItems: CheckboxItem[];
  moodItems: CheckboxItem[];
  tagItems: CheckboxItem[];
  onGenreChange: (index: number, checked: boolean) => void;
  onMoodChange: (index: number, checked: boolean) => void;
  onTagChange: (index: number, checked: boolean) => void;
  hasActiveFilters: boolean;
  onClearAll: () => void;
}) {
  return (
    <div className="flex-1 p-6">
      <h2 className="mb-5 flex items-center gap-2 font-semibold text-text-primary">
        Filters
      </h2>
      <div className="flex flex-col gap-4">
        <Select
          value={ownership}
          onValueChange={(value) =>
            onOwnershipChange(value as BackgroundMusicOwnershipFilterDto)
          }
        >
          <SelectTrigger
            className={cn(
              "w-full",
              ownership === "ALL" && "text-text-secondary",
            )}
          >
            <SelectValue>{ownershipLabels[ownership]}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{ownershipLabels.ALL}</SelectItem>
            <SelectItem value="USER">{ownershipLabels.USER}</SelectItem>
            <SelectItem value="LIBRARY">{ownershipLabels.LIBRARY}</SelectItem>
            <SelectItem value="BEAT_SYNC">
              {ownershipLabels.BEAT_SYNC}
            </SelectItem>
          </SelectContent>
        </Select>
        <DropdownMenuCheckboxes
          title="Genres"
          items={genreItems}
          onCheckedChange={onGenreChange}
        />
        <DropdownMenuCheckboxes
          title="Moods"
          items={moodItems}
          onCheckedChange={onMoodChange}
        />
        <DropdownMenuCheckboxes
          title="Tags"
          items={tagItems}
          onCheckedChange={onTagChange}
        />
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="mx-auto mt-2 text-left text-sm font-medium text-text-secondary"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

function DropdownMenuCheckboxes({
  title,
  items,
  onCheckedChange,
}: {
  title: string;
  items: CheckboxItem[];
  onCheckedChange: (index: number, checked: boolean) => void;
}) {
  const isEmpty = items.length === 0;
  const checkedItems = items.filter((item) => item.checked);
  const hasCheckedItems = checkedItems.length > 0;

  const [firstCheckedItem] = checkedItems;
  const triggerLabel = !firstCheckedItem
    ? title
    : checkedItems.length === 1
      ? firstCheckedItem.label
      : `${firstCheckedItem.label} +${checkedItems.length - 1}`;

  const handleReset = () => {
    items.forEach((_, index) => {
      onCheckedChange(index, false);
    });
  };

  const triggerButton = (
    <button
      disabled={isEmpty}
      className={cn(
        buttonVariants({ variant: "outline", size: "sm" }),
        "flex h-10 w-full justify-between rounded-md px-3 py-2 text-sm font-normal transition-none",
        !hasCheckedItems && "text-text-secondary",
      )}
    >
      <span className="truncate">{triggerLabel}</span>
      <IconChevronDown className="shrink-0 text-text-secondary" />
    </button>
  );

  if (isEmpty) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-block w-full">
            {triggerButton}
          </span>
        </TooltipTrigger>
        <TooltipContent>No {title.toLowerCase()} available</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent className="max-w-96 min-w-56" side="bottom">
        {items.map((item, index) => (
          <DropdownMenuCheckboxItem
            onSelect={(event) => event.preventDefault()}
            key={index}
            checked={item.checked}
            onCheckedChange={(checked) => {
              onCheckedChange(index, !!checked);
            }}
          >
            {item.label}
          </DropdownMenuCheckboxItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleReset}>Reset Filters</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
